// SPDX-License-Identifier: AGPL-3.0-or-later

import * as Modal from '@app/features/app/components/dialogs/Modal';
import * as MessageCommands from '@app/features/messaging/commands/MessageCommands';
import styles from '@app/features/poll/components/modals/CreatePollModal.module.css';
import {DEFAULT_POLL_EMOJIS, formatPollMessage, type PollOption} from '@app/features/poll/types/PollTypes';
import {Button} from '@app/features/ui/button/Button';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {Switch} from '@app/features/ui/components/form/FormSwitch';
import * as SnowflakeUtils from '@fluxer/snowflake/src/SnowflakeUtils';
import {PlusIcon, TrashIcon} from '@phosphor-icons/react';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import {useCallback, useMemo, useState} from 'react';

interface CreatePollModalProps {
	channelId: string;
}

const DURATION_OPTIONS = [
	{label: '1 hr', valueMs: 1 * 60 * 60 * 1000},
	{label: '4 hrs', valueMs: 4 * 60 * 60 * 1000},
	{label: '24 hrs', valueMs: 24 * 60 * 60 * 1000},
	{label: '3 days', valueMs: 3 * 24 * 60 * 60 * 1000},
	{label: '1 week', valueMs: 7 * 24 * 60 * 60 * 1000},
];

const PRESET_EMOJIS = ['1️⃣', '2️⃣', '3️⃣', '4️⃣', '5️⃣', '6️⃣', '7️⃣', '8️⃣', '9️⃣', '🔟', '🎮', '🍕', '🚀', '❤️', '🔥', '👍', '👎', '🎉'];

export const CreatePollModal = observer(({channelId}: CreatePollModalProps) => {
	const [question, setQuestion] = useState('');
	const [options, setOptions] = useState<Array<PollOption>>([
		{id: '1', text: '', emoji: '1️⃣'},
		{id: '2', text: '', emoji: '2️⃣'},
	]);
	const [allowMultiple, setAllowMultiple] = useState(false);
	const [selectedDurationMs, setSelectedDurationMs] = useState(24 * 60 * 60 * 1000);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const handleOptionTextChange = (id: string, text: string) => {
		setOptions((prev) =>
			prev.map((opt) => (opt.id === id ? {...opt, text} : opt)),
		);
	};

	const handleCycleEmoji = (id: string) => {
		setOptions((prev) =>
			prev.map((opt) => {
				if (opt.id !== id) return opt;
				const currentIndex = PRESET_EMOJIS.indexOf(opt.emoji);
				const nextEmoji = PRESET_EMOJIS[(currentIndex + 1) % PRESET_EMOJIS.length];
				return {...opt, emoji: nextEmoji};
			}),
		);
	};

	const handleAddOption = () => {
		if (options.length >= 10) return;
		const nextId = String(options.length + 1);
		const defaultEmoji =
			DEFAULT_POLL_EMOJIS[options.length] || '⭐';
		setOptions((prev) => [
			...prev,
			{id: nextId, text: '', emoji: defaultEmoji},
		]);
	};

	const handleDeleteOption = (id: string) => {
		if (options.length <= 2) return;
		setOptions((prev) => prev.filter((opt) => opt.id !== id));
	};

	const validOptions = useMemo(
		() => options.filter((opt) => opt.text.trim().length > 0),
		[options],
	);

	const canSubmit = question.trim().length > 0 && validOptions.length >= 2;

	const handleSubmit = useCallback(async () => {
		if (!canSubmit || isSubmitting) return;
		setIsSubmitting(true);
		try {
			const expiresAt = Date.now() + selectedDurationMs;
			const pollData = {
				question: question.trim(),
				options: validOptions.map((opt, index) => ({
					id: String(index + 1),
					text: opt.text.trim(),
					emoji: opt.emoji,
				})),
				allowMultiple,
				expiresAt,
			};

			const messageContent = formatPollMessage(pollData);
			const nonce = SnowflakeUtils.fromTimestamp(Date.now());
			MessageCommands.reserveSend(channelId, nonce);
			await MessageCommands.send(channelId, {content: messageContent, nonce});
			ModalCommands.pop();
		} catch {
			setIsSubmitting(false);
		}
	}, [allowMultiple, canSubmit, channelId, isSubmitting, question, selectedDurationMs, validOptions]);

	return (
		<Modal.Root size="medium" data-flx="poll.create-poll-modal">
			<Modal.Header title="Create a Poll" />

			<Modal.Content>
				<div className={styles.container}>
					<div className={styles.fieldGroup}>
						<label className={styles.label}>Question</label>
						<input
							type="text"
							className={styles.input}
							placeholder="What would you like to ask?"
							value={question}
							onChange={(e) => setQuestion(e.target.value)}
							maxLength={300}
							autoFocus
						/>
					</div>

					<div className={styles.fieldGroup}>
						<label className={styles.label}>
							Answers ({validOptions.length}/{options.length})
						</label>
						<div className={styles.optionsContainer}>
							{options.map((option, idx) => (
								<div key={option.id} className={styles.optionRow}>
									<button
										type="button"
										className={styles.emojiSelectButton}
										onClick={() => handleCycleEmoji(option.id)}
										title="Click to cycle emoji"
									>
										{option.emoji}
									</button>
									<input
										type="text"
										className={styles.optionInput}
										placeholder={`Option ${idx + 1}`}
										value={option.text}
										onChange={(e) => handleOptionTextChange(option.id, e.target.value)}
										maxLength={120}
									/>
									{options.length > 2 && (
										<button
											type="button"
											className={styles.deleteButton}
											onClick={() => handleDeleteOption(option.id)}
											title="Remove answer"
										>
											<TrashIcon size={18} />
										</button>
									)}
								</div>
							))}
						</div>

						{options.length < 10 && (
							<button
								type="button"
								className={styles.addOptionButton}
								onClick={handleAddOption}
							>
								<PlusIcon size={16} weight="bold" />
								Add Answer
							</button>
						)}
					</div>

					<div className={styles.fieldGroup}>
						<label className={styles.label}>Poll Duration</label>
						<div className={styles.durationGrid}>
							{DURATION_OPTIONS.map((dur) => (
								<button
									key={dur.valueMs}
									type="button"
									className={clsx(
										styles.durationButton,
										selectedDurationMs === dur.valueMs && styles.durationButtonActive,
									)}
									onClick={() => setSelectedDurationMs(dur.valueMs)}
								>
									{dur.label}
								</button>
							))}
						</div>
					</div>

					<div className={styles.switchRow}>
						<div className={styles.switchLabel}>
							<span className={styles.switchTitle}>Allow multiple answers</span>
							<span className={styles.switchSub}>
								Users can vote for more than one choice
							</span>
						</div>
						<Switch
							value={allowMultiple}
							onChange={setAllowMultiple}
						/>
					</div>
				</div>
			</Modal.Content>

			<Modal.Footer>
				<Button
					variant="secondary"
					onClick={() => ModalCommands.pop()}
					disabled={isSubmitting}
				>
					Cancel
				</Button>
				<Button
					variant="primary"
					onClick={handleSubmit}
					disabled={!canSubmit || isSubmitting}
				>
					{isSubmitting ? 'Posting...' : 'Post Poll'}
				</Button>
			</Modal.Footer>
		</Modal.Root>
	);
});
